import { Button, Grid } from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef } from "react";

import ReactFlow, { Controls, Handle, Position } from "reactflow";

import "reactflow/dist/style.css";
import { ChemicalFormulaRenderer } from "../../lib/prettifier";
import { moleculeEdges, moleculeNodes } from "../../types/moleculeGraph";
import { GameContext } from "../../provider/GameProvider";
import { TabHeader } from "./TabHeader";
import {
  getUnlockedMoleculeNodes,
  isMoleculeUnlocked,
} from "../../lib/calculator";
import { executeMoleculeCreation } from "../../lib/resets";
import { Hand } from "@phosphor-icons/react/dist/ssr";
import {
  isAchievementUnlocked,
  unlockAchievement,
} from "../../types/achievements";
import { SnackbarContext } from "../../provider/SnackbarProvider";

const StartNode = ({ data }: { data: any }) => {
  return (
    <>
      <NodeButton data={data} />
      <Handle type="source" position={Position.Bottom} id="a" />
    </>
  );
};

const MidNode = ({ data }: { data: any }) => {
  return (
    <>
      <Handle type="target" position={Position.Top} id="a" />
      <NodeButton data={data} />
      <Handle type="source" position={Position.Bottom} id="a" />
    </>
  );
};
const MidNodeTopOutput = ({ data }: { data: any }) => {
  return (
    <>
      <Handle type="target" position={Position.Top} id="a" />
      <NodeButton data={data} />
      <Handle type="source" position={Position.Top} id="a" />
    </>
  );
};

const EndNode = ({ data }: { data: any }) => {
  return (
    <>
      <Handle type="target" position={Position.Top} id="a" />
      <NodeButton data={data} />
    </>
  );
};

const nodeTypes = {
  startNode: StartNode,
  midNode: MidNode,
  endNode: EndNode,
  midNodeTopOutput: MidNodeTopOutput,
};
export function MoleculeTab() {
  const { game, setGame } = useContext(GameContext);
  const { showSnackbar } = useContext(SnackbarContext);

  const parentRef = useRef<HTMLDivElement>(null);
  const transformedNodes = useMemo(() => {
    if (parentRef.current) {
      const transformedNodes = moleculeNodes.map((node) => {
        return {
          ...node,
          position: {
            ...node.position,
            x:
              node.position.x +
              (parentRef.current?.offsetWidth ?? 300) / 2 -
              100,
          },
        };
      });
      return transformedNodes;
    }
  }, [parentRef.current]);

  useEffect(() => {
    if (
      isMoleculeUnlocked("Hydrogen", game) &&
      isMoleculeUnlocked("Oxygen", game)
    ) {
      const newlyUnlocked = unlockAchievement(
        14,
        game.unlockedAchievementIds,
        setGame
      );
      if (newlyUnlocked) {
        showSnackbar("Achievement Unlocked!");
      }
    }
  });

  return (
    <Grid container spacing={2} style={{ overflowY: "hidden" }}>
      <TabHeader />
      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          justifyContent: "flex-start",
        }}
      >
        <div
          style={{
            fontSize: 12,
          }}
        >
          Buying a Molecule-Upgrade will reset your Molecule-Creation run!
        </div>
      </Grid>
      <Grid item xs={7} />
      <Grid item xs={12}>
        <div
          style={{
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255,255,255,0.1)",
            borderRadius: 20,
          }}
          ref={parentRef}
        >
          <ReactFlow
            nodes={transformedNodes}
            edges={moleculeEdges}
            nodesConnectable={false}
            zoomOnDoubleClick={false}
            deleteKeyCode={null}
            nodeTypes={nodeTypes}
            minZoom={0.65}
            //fitView
            fitViewOptions={{}}
            elementsSelectable={false}
            defaultViewport={{
              x: 200,
              y: 0,
              zoom: 0.65,
            }}
          >
            <Controls position="top-right" showInteractive={false} />
          </ReactFlow>
        </div>
      </Grid>
      <Grid item xs={12}>
        <div
          style={{
            backgroundColor: "#111111",
            height: 200,
            width: "100%",
            marginTop: -100,
            zIndex: 10000000000000,
          }}
        ></div>
      </Grid>
    </Grid>
  );
}

function NodeButton({ data }: { data: any }) {
  const unit = data.cost === 1 ? "Molecule" : "Molecules";
  const { game, setGame, setTimeProgressArray } = React.useContext(GameContext);

  const multipleUnlocked = useMemo(() => {
    return isMoleculeUnlocked("Ethanol", game);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.molecule.graphNodes]);

  const nodeUnlocked = useMemo(() => {
    return isMoleculeUnlocked(data.label, game);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.molecule.graphNodes]);

  const maxed = useMemo(() => {
    return game.molecule.graphNodes[data.label] >= data.max;
  }, [data.label, data.max, game.molecule.graphNodes]);

  const cost = useMemo(() => {
    if (nodeUnlocked) {
      const mValue = game.molecule.graphNodes[data.label];
      if (!maxed && multipleUnlocked) {
        return data.cost * Math.pow(5, mValue);
      }
      return 0;
    }
    return data.cost;
  }, [
    data.cost,
    data.label,
    game.molecule.graphNodes,
    maxed,
    multipleUnlocked,
    nodeUnlocked,
  ]);

  const handleClick = () => {
    if (game.molecule.value.gte(cost)) {
      setGame((prev) => {
        return {
          ...prev,
          molecule: {
            ...prev.molecule,
            value: prev.molecule.value.minus(cost),
            graphNodes: {
              ...prev.molecule.graphNodes,
              [data.label]: (prev.molecule.graphNodes[data.label] ?? 0) + 1,
            },
          },
        };
      });
      executeMoleculeCreation(game, setGame, setTimeProgressArray);
    }
  };
  const isUnlocked = useMemo(() => {
    const unlockedMolecules = getUnlockedMoleculeNodes(game);
    const isUnlocked = data.unlocked.every((molecule: any) =>
      unlockedMolecules.has(molecule)
    );
    return isUnlocked;
  }, [game.molecule.graphNodes]);

  const isVisible = useMemo(() => {
    const unlockedMolecules = getUnlockedMoleculeNodes(game);
    const isVisible = data.visible.every((molecule: any) =>
      unlockedMolecules.has(molecule)
    );
    let achievementUnlocked = true;
    if (data.achievement) {
      achievementUnlocked = isAchievementUnlocked(
        data.achievement,
        game.unlockedAchievementIds
      );
    }
    return isVisible && achievementUnlocked;
  }, [game.molecule.graphNodes]);

  if (!isVisible) return null;
  return (
    <Button
      style={{
        width: 200,
        backgroundColor: nodeUnlocked
          ? !maxed && multipleUnlocked
            ? "rgba(255, 140, 0, 0.5)"
            : "darkorange"
          : "transparent",
        height: 150,
        borderColor: "darkorange",
        color: "inherit",
      }}
      onClick={() => handleClick()}
      disabled={!isUnlocked || maxed}
    >
      <div>
        <div
          style={{
            textTransform: "none",
            lineHeight: "1.2",
            fontSize: 16,
            fontWeight: "bold",
          }}
        >
          {data.label}
          <div style={{ fontSize: 12, fontWeight: "normal" }}>
            <ChemicalFormulaRenderer formula={data.formula} />
          </div>
        </div>
        <div style={{ textTransform: "none", fontSize: 12 }}>
          {data.description}
        </div>
        {cost > 0 ? (
          <div style={{ textTransform: "none" }}>
            Cost: {cost} {unit}
          </div>
        ) : (
          <div style={{ textTransform: "none" }}></div>
        )}
      </div>
    </Button>
  );
}
